import * as Api from '@tableau/api-internal-contract-js';
import { DashboardObjectType } from '@tableau/api-internal-contract-js';
import * as PresLayerEnums from '../../gen-src/preslayer/Enums';
import * as PresLayer from '../../gen-src/preslayer/PresModels';
import { PresLayerToApiEnumMappings as EnumMappings, ShouldThrow } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';

/**
 * Class containing helper methods for converting from PresLayerPresModels to their ExtensionsApi or EmbeddingApi PresModels equivalents
 *
 * @export
 * @class PresLayerToSharedApiConverter
 */
export class PresLayerToSharedApiConverter {
  public static convertFieldCaptionPair(plFieldCaptionPair: PresLayer.FieldCaptionPairPresModel): string {
    return plFieldCaptionPair.fn;
  }

  public static convertVisualId(plVisualId: PresLayer.VisualIDPresModel): Api.VisualId {
    const result: Api.VisualId = {
      worksheet: plVisualId.worksheet,
      dashboard: plVisualId.dashboard,
      storyboard: plVisualId.storyboard,
      flipboardZoneID: plVisualId.flipboardZoneId,
      storyPointID: plVisualId.storyPointId,
    };

    return result;
  }

  public static convertFilterEvent(plFilterEvent: PresLayer.FilterChangeEventPresModel): Api.FilterEvent {
    const result: Api.FilterEvent = {
      visualId: PresLayerToSharedApiConverter.convertVisualId(plFilterEvent.visualIdPresModel),
      fieldName: plFilterEvent.fieldCaption,
      fieldId: plFilterEvent.globalFieldName,
    };

    return result;
  }

  public static convertDashboardPresModelToZones(
    plDashboard: PresLayer.DashboardPresModel | null,
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    if (!plDashboard) {
      // plDashboard is null for a blank Story.
      // plDashboard.zones is undefined for a blank Dashboard.
      return new Array<Api.DashboardZone>();
    }

    return PresLayerToSharedApiConverter.convertZoneMapToZones(plDashboard.zones, worksheetUrls);
  }

  public static convertZoneMapToZones(
    zones: { [key: string]: PresLayer.ZonePresModel } | undefined,
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    const result = new Array<Api.DashboardZone>();
    if (!zones) {
      return result;
    }

    for (const zoneKey of Object.keys(zones)) {
      const zone = zones[zoneKey];

      const zoneCommon = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.zoneCommon);

      const apiZone: Api.DashboardZone = {
        name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zoneCommon.name),
        zoneId: zone.zoneId,
        zoneType: EnumMappings.zoneType.convert(zoneCommon.zoneType, ShouldThrow.No),
        height: zoneCommon.h,
        width: zoneCommon.w,
        x: zoneCommon.x,
        y: zoneCommon.y,
        isFloating: zoneCommon.isFloating,
        isVisible: zone.isVisible,
      };

      // Fix up Viz & Extension zones
      if (zoneCommon.zoneType === PresLayerEnums.ZoneType.Viz) {
        const worksheet = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.worksheet);
        const sheetInfo: Api.SheetInfo = {
          name: worksheet,
          sheetType: Api.SheetType.Worksheet,
        };
        // worksheetUrls is only used by Embedding
        if (worksheetUrls && worksheetUrls[worksheet]) {
          sheetInfo.url = worksheetUrls[worksheet];
        }
        apiZone.sheetInfo = sheetInfo;
      } else if (
        zoneCommon.zoneType === PresLayerEnums.ZoneType.DashboardObject &&
        zoneCommon.dashboardObjectType === PresLayerEnums.DocDashboardObjectTypes.DBO_Extension
      ) {
        apiZone.zoneType = DashboardObjectType.Extension;
      }

      result.push(apiZone);
    }

    return result;
  }

  public static convertDashboardToZones(
    plDashboardZoneInfos: PresLayer.DashboardZoneInfoPresModel[],
    worksheetUrls?: Map<string, string>,
  ): Array<Api.DashboardZone> {
    const result = new Array<Api.DashboardZone>();

    for (const zone of plDashboardZoneInfos) {
      const apiZone: Api.DashboardZone = {
        zoneId: zone.zoneId,
        name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.name),
        zoneType: EnumMappings.zoneType.convert(zone.zoneType, ShouldThrow.No),
        height: zone.h,
        width: zone.w,
        x: zone.x,
        y: zone.y,
        isFloating: zone.isFloating,
        isVisible: zone.isVisible,
      };

      // Fix up Viz & Extension zones
      const worksheet = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(zone.worksheet);
      if (zone.zoneType === PresLayerEnums.ZoneType.Viz) {
        const sheetInfo: Api.SheetInfo = {
          name: worksheet,
          sheetType: Api.SheetType.Worksheet,
        };
        // worksheetUrls is only used by Embedding
        if (worksheetUrls && worksheetUrls[worksheet]) {
          sheetInfo.url = worksheetUrls[worksheet];
        }
        apiZone.sheetInfo = sheetInfo;
      } else if (
        zone.zoneType === PresLayerEnums.ZoneType.DashboardObject &&
        zone.dashboardObjectType === PresLayerEnums.DocDashboardObjectTypes.DBO_Extension
      ) {
        apiZone.zoneType = DashboardObjectType.Extension;
      }

      result.push(apiZone);
    }

    return result;
  }
}
